import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useStyle, useCreateStyle, useUpdateStyle, useUploadStyleThumbnail, apiClient } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { I18nFieldsSection } from '../components/I18nFieldsSection'
import { X, ImagePlus } from 'lucide-react'
import { toast } from 'sonner'

export function StyleForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: style } = useStyle(id!)
  const createMutation = useCreateStyle()
  const updateMutation = useUpdateStyle(id!)
  const uploadThumbnailMutation = useUploadStyleThumbnail(id!)

  type StyleFormData = {
    id: string
    name: string
    description: string
    nameI18n: Record<string, string>
    descriptionI18n: Record<string, string>
    promptSuffix: string
    listOrder: number
    enabled: boolean
  }

  const baseFormData = useMemo<StyleFormData>(
    () =>
      style
        ? {
            id: style.id,
            name: style.name,
            description: style.description,
            nameI18n: style.nameI18n ?? {},
            descriptionI18n: style.descriptionI18n ?? {},
            promptSuffix: style.promptSuffix,
            listOrder: style.listOrder,
            enabled: style.enabled,
          }
        : {
            id: '',
            name: '',
            description: '',
            nameI18n: {},
            descriptionI18n: {},
            promptSuffix: '',
            listOrder: 0,
            enabled: true,
          },
    [style],
  )
  const [draftFormData, setDraftFormData] = useState<StyleFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreviewOverride, setThumbnailPreviewOverride] = useState<string | null>(null)
  const baseThumbnailPreview =
    style?.thumbnailUrl ? `${style.thumbnailUrl}?v=${new Date(style.updatedAt).getTime()}` : ''
  const thumbnailPreview = thumbnailPreviewOverride ?? baseThumbnailPreview

  const updateField = <K extends keyof StyleFormData>(key: K, value: StyleFormData[K]) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const updateI18nField = (field: 'nameI18n' | 'descriptionI18n', lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, [field]: { ...base[field], [lang]: text } }
    })
  }

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setThumbnailFile(file)
    const reader = new FileReader()
    reader.onload = () => setThumbnailPreviewOverride(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      let styleId = id

      // 1. 스타일 생성 또는 수정
      if (isEdit) {
        await updateMutation.mutateAsync({
          name: formData.name,
          description: formData.description,
          nameI18n: formData.nameI18n,
          descriptionI18n: formData.descriptionI18n,
          promptSuffix: formData.promptSuffix,
          listOrder: formData.listOrder,
          enabled: formData.enabled,
        })
        toast.success('스타일이 수정되었습니다')
      } else {
        const created = await createMutation.mutateAsync(formData)
        styleId = created.id
        toast.success('스타일이 생성되었습니다')
      }

      // 2. 썸네일 업로드 (있는 경우)
      if (thumbnailFile && styleId) {
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(thumbnailFile)
        })

        if (isEdit) {
          await uploadThumbnailMutation.mutateAsync(dataUrl)
        } else {
          // 생성 모드: 직접 API 호출 (훅은 id가 없어서 사용 불가)
          await apiClient.post(`/admin/styles/${styleId}/thumbnail`, { dataUrl })
        }
        toast.success('썸네일이 업로드되었습니다')
      }

      // 수정 시 상세 페이지로, 생성 시 목록으로 이동
      if (isEdit) {
        navigate(`/admin/styles/${id}/view`)
      } else {
        navigate('/admin/styles')
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다')
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">
          {isEdit ? '스타일 수정' : '새 스타일'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg border">
        {/* ID (create only) */}
        {!isEdit && (
          <div className="space-y-2">
            <Label htmlFor="id">ID * (영문 소문자, 언더스코어)</Label>
              <Input
                id="id"
                value={formData.id}
                onChange={(e) => updateField('id', e.target.value)}
                required
                pattern="[a-z_]+"
                placeholder="photo, ppt 등"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>이름 *</Label>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            placeholder="발표자료, 실사 등"
          />
        </div>

        <div className="space-y-2">
          <Label>설명 *</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            required
            rows={2}
          />
        </div>

        <I18nFieldsSection
          value={{ nameI18n: formData.nameI18n, descriptionI18n: formData.descriptionI18n }}
          onChange={updateI18nField}
          withDescription
        />

        <div className="space-y-2">
          <Label>프롬프트 접미사 *</Label>
          <Textarea
            value={formData.promptSuffix}
            onChange={(e) => updateField('promptSuffix', e.target.value)}
            required
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label>정렬 순서</Label>
          <Input
            type="number"
            value={formData.listOrder}
            onChange={(e) => updateField('listOrder', Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>

        {/* Thumbnail */}
        <div className="space-y-2">
          <Label>썸네일 (256x256)</Label>
          <div
            className={`relative w-32 h-32 rounded-lg overflow-hidden ${
              thumbnailPreview
                ? 'group'
                : 'border-2 border-dashed border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer'
            }`}
            onClick={!thumbnailPreview ? () => document.getElementById('thumbnail')?.click() : undefined}
          >
            {thumbnailPreview ? (
              <>
                <img
                  src={thumbnailPreview}
                  alt="썸네일 미리보기"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        document.getElementById('thumbnail')?.click()
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 변경"
                    >
                      <ImagePlus className="w-4 h-4 text-slate-700" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setThumbnailFile(null)
                        setThumbnailPreviewOverride('')
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 삭제"
                    >
                      <X className="w-4 h-4 text-slate-700" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <ImagePlus className="w-6 h-6 text-slate-400" />
                <span className="text-xs text-slate-500">썸네일 업로드</span>
              </div>
            )}
          </div>
          <input
            type="file"
            id="thumbnail"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="hidden"
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => updateField('enabled', e.target.checked)}
            className="w-4 h-4"
          />
          <Label>활성화</Label>
        </div>

        <div className="flex space-x-4">
          <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
            {isEdit ? '수정' : '생성'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
